import { getAccessToken } from "../../utils/auth";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { IconButton, CircularProgress, Tooltip as MuiTooltip } from "@mui/material";
import axios from "../../utils/api.js";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

const DaranswerData = () => {
  const [distributors, setDistributors] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState("");
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const chartSectionRef = useRef(null);

  useEffect(() => {
    const fetchDistributors = async () => {
      try {
        const response = await axios.get("/distributors", {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        setDistributors(response.data);
      } catch (err) {
        setError("Failed to load distributors");
      }
    };
    fetchDistributors();
  }, []);

  const fetchAnswers = useCallback(async () => {
    if (!selectedDistributor) {
      setError("Please select a distributor");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = getAccessToken();
      if (!token) {
        setError("Unauthorized: No token found");
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `/answers-by-distributor/${selectedDistributor}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        setAnswers(response.data);
      } else {
        setError("Failed to fetch answers");
      }
    } catch (err) {
      setError("No Data For This Distributor");
    } finally {
      setLoading(false);
    }
  }, [selectedDistributor]);

  const COLORS = [
    "#667eea",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#764ba2",
    "#10b981",
  ];

  const getChartData = () => {
    return answers.map((submission, index) => {
      const customerName =
        submission.answers?.[3]?.answer || "Unknown Customer";
      const totalWeightage =
        (submission.decisionMakingAuthority || []).reduce(
          (total, item) => total + (item.weightage || 0),
          0
        ) +
        (submission.abilityToPurchase || []).reduce(
          (total, item) => total + (item.weightage || 0),
          0
        ) +
        (submission.readinessToBuy || []).reduce(
          (total, item) => total + (item.weightage || 0),
          0
        );

      return {
        name: customerName,
        value: totalWeightage,
        fill: COLORS[index % COLORS.length],
      };
    });
  };

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  const tableHeaderStyle = {
    padding: "12px 16px",
    textAlign: "left",
    fontSize: 13,
    fontWeight: 600,
  };

  const tableCellStyle = {
    padding: "12px 16px",
    fontSize: 14,
    color: "#334155",
    borderBottom: "1px solid #e2e8f0",
  };

  if (loading) {
    return (
      <div
        style={{
          ...pageStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <CircularProgress size={48} sx={{ color: "#667eea" }} />
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      {answers.length > 0 && (
        <MuiTooltip title="Move down to view Potential Level Distribution chart">
          <IconButton
            onClick={() =>
              chartSectionRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              })
            }
            style={{
              position: "absolute",
              top: 24,
              right: 24,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
              color: "#667eea",
              zIndex: 10,
            }}
          >
            <KeyboardArrowDownIcon fontSize="medium" />
          </IconButton>
        </MuiTooltip>
      )}

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Select Distributor & View DAR Analysis
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          View weightage summary and distribution per customer
        </p>
      </div>

      <div
        style={{
          maxWidth: 720,
          margin: "0 auto 24px",
          display: "flex",
          gap: 12,
          alignItems: "flex-end",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <select
          value={selectedDistributor}
          onChange={(e) => setSelectedDistributor(e.target.value)}
          style={{
            padding: "0.65rem 2.25rem 0.65rem 0.85rem",
            fontSize: "1rem",
            minWidth: 260,
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            outline: "none",
            backgroundColor: "#fff",
            appearance: "none",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%2364748b' height='24' viewBox='0 0 24 24' width='24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 0.75rem center",
            backgroundSize: "1.25rem",
            cursor: "pointer",
          }}
        >
          <option value="">Select Distributor</option>
          {distributors.map((distributor, index) => (
            <option key={index} value={distributor.name}>
              {distributor.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={fetchAnswers}
          style={{
            padding: "0.65rem 1.25rem",
            fontSize: "1rem",
            fontWeight: 600,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
          }}
        >
          Search
        </button>
      </div>

      {error && (
        <p
          style={{
            textAlign: "center",
            color: "#dc2626",
            fontSize: "0.95rem",
            margin: "0 0 20px",
          }}
        >
          {error}
        </p>
      )}

      {answers.length > 0 && (
        <>
          <div
            style={{
              maxWidth: 1000,
              margin: "0 auto 32px",
              backgroundColor: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#1e3a5f", color: "#fff" }}>
                  <th style={tableHeaderStyle}>S.No</th>
                  <th style={tableHeaderStyle}>Distributor Name</th>
                  <th style={tableHeaderStyle}>Organisation</th>
                  <th style={tableHeaderStyle}>Customer</th>
                  <th style={tableHeaderStyle}>Total Weightage</th>
                </tr>
              </thead>
              <tbody>
                {answers.map((submission, index) => {
                  const organisation =
                    submission.answers?.[0]?.answer || "N/A";
                  const customerName =
                    submission.answers?.[3]?.answer || "N/A";
                  const totalWeightage =
                    (submission.decisionMakingAuthority || []).reduce(
                      (total, item) => total + (item.weightage || 0),
                      0
                    ) +
                    (submission.abilityToPurchase || []).reduce(
                      (total, item) => total + (item.weightage || 0),
                      0
                    ) +
                    (submission.readinessToBuy || []).reduce(
                      (total, item) => total + (item.weightage || 0),
                      0
                    );

                  return (
                    <tr
                      key={index}
                      style={{
                        backgroundColor:
                          index % 2 === 0 ? "#fff" : "#f8fafc",
                      }}
                    >
                      <td style={tableCellStyle}>{index + 1}</td>
                      <td style={tableCellStyle}>
                        {submission.distributorName}
                      </td>
                      <td style={tableCellStyle}>{organisation}</td>
                      <td style={tableCellStyle}>{customerName}</td>
                      <td style={tableCellStyle}>{totalWeightage}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div
            ref={chartSectionRef}
            style={{
              maxWidth: 1000,
              margin: "0 auto",
              backgroundColor: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              padding: 24,
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <h3
              style={{
                margin: "0 0 20px",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#1e293b",
              }}
            >
              Potential Level Distribution
            </h3>
            <BarChart
              width={600}
              height={450}
              data={getChartData()}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip
                formatter={(value) => [`Weightage: ${value}`]}
                contentStyle={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              />
              <Legend />
              <Bar
              dataKey="value"
              radius={[4, 4, 0, 0]}
              fill={(entry) => entry.fill || "#667eea"}
            />
            </BarChart>
          </div>
        </>
      )}
    </div>
  );
};

export default DaranswerData;
